import * as React from 'react';
import { observer } from 'mobx-react';
import {
    ITranslationsCollection,
    configConsumer,
    addConfig,
    addContext,
    addTranslation,
    translationConsumer
} from '@estee/elc-service';
import { OrderRender } from '@estee/elc-layouts';
import { root } from '@estee/elc-universal-utils';
import { classes } from '~utils/Classes';
import styled from 'styled-components';
import { getViewRegistry } from '~utils/orderableUtils/getViewRegistry';
import { IBaseFeatureData, IOrderConfigCollection } from '~interfaces/IOrderableComponents';
import { ProductBriefViewController } from '~controllers/ProductBriefViewController';
import { action, observable } from 'mobx';
import { CheckVisibility } from '@estee/elc-check-visibility';
import { productBriefTranslations } from '~utils/orderableUtils/translations';
import { productBriefconfigs } from '~utils/orderableUtils/configs';
import { ProductQuickView } from '../../internal/views/product-quickview/ProductQuickView';
import {
    IProductBriefConfig,
    IProductBrief,
    IProductBriefViewControllerData
} from '~interfaces/IProductBrief';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/ProductService';
import { ComponentTypes } from '~constants/ComponentTypes';
import { mergeConfigs } from '~utils/ConfigUtils';
import { PRODUCT_BRIEF } from '~constants/DataTestIdAttributes';

interface IWrapperProps {
    maxHeight: number;
}

const ProductBriefWrapper = styled.div``;
const InitialWrapper = styled.div<IWrapperProps>`
    height: ${(props) => (props.maxHeight ? `${props.maxHeight}px` : '100%')};
`;

type IProductBriefData = IProductBriefViewControllerData;

export interface IProductBriefFeature extends IBaseFeatureData, IProductBriefData {}

@addContext()
@addConfig(
    ['prodcatConfig', 'features', 'itemMaxQuantity', 'productBenefitTypes', 'showBestShadeMessage'],
    mergeConfigs
)
@configConsumer(productBriefconfigs)
@addTranslation(productBriefTranslations)
@translationConsumer(productBriefTranslations)
@observer
class ProductBrief extends React.Component<IProductBrief> {
    @observable private isItemInView: boolean = false;
    public static displayName: string;
    private productBriefOrder: IOrderConfigCollection;
    @observable private viewController: ProductBriefViewController;
    private productBriefRef: React.RefObject<HTMLDivElement>;

    constructor(props: IProductBrief) {
        super(props);
        const { config: briefConfig } = this.props;
        const { componentElementsOrderConfig } = briefConfig as IProductBriefConfig;
        const productBriefOrderConfig =
            componentElementsOrderConfig && componentElementsOrderConfig.productBrief;
        this.productBriefOrder = productBriefOrderConfig || {};
        this.viewController = diContainer.get(serviceNames.productBriefViewController);
        this.viewController.initialize(briefConfig);
        this.productBriefRef = React.createRef();
    }

    @action
    private onVisible = () => {
        this.isItemInView = true;
    };

    public async componentDidMount() {
        if (!this.viewController.isLoaded) {
            await this.viewController.initialize(this.props.config);
        }
    }

    private renderQuickView() {
        const quickViewProps = {
            product: this.viewController.product,
            onShadeSelect: this.viewController.handleShadeSelect,
            onSizeSelect: this.viewController.handleSizeSelect,
            onProductQuickViewClose: this.viewController.closeProductQuickView,
            selectedSku: this.viewController.selectedSku
        };

        return <ProductQuickView {...quickViewProps} />;
    }

    private renderBrief() {
        if (!this.viewController.isLoaded) {
            return null;
        }
        const productBriefClassNames = classes('product-brief', this.props.className);
        const {
            product: { productId }
        } = this.viewController;
        const { lazyLoadImageDesktop, lazyLoadImageMobile, forceLazyLoadImage } = this.props.config;

        const clickableProps = this.props.config.enableClickableBrief
            ? {
                  clickAction: this.viewController.navigateToProduct,
                  href: this.viewController.targetUrl
              }
            : {};

        const featureConfig: IBaseFeatureData = {
            ...this.viewController.data,
            translations: this.props.translations as ITranslationsCollection,
            setAutoReplenishOption: this.viewController.setAutoReplenishOption,
            getCustomSizeMedia: this.viewController.getCustomSizeMedia,
            navigateToProduct: this.viewController.navigateToProduct,
            onQuickViewButtonClick: this.viewController.onQuickViewButtonClick,
            handleShadeSelect: this.viewController.handleShadeSelect,
            handleSizeSelect: this.viewController.handleSizeSelect,
            emitSocialShareIconTriggered: this.viewController.emitSocialShareIconTriggered,
            selectShade: this.viewController.selectShade,
            handleLiveChatCTAClick: this.viewController.handleLiveChatCTAClick,
            handleFavoriteClick: this.viewController.handleFavoriteClick,
            toggleDescription: this.viewController.toggleDescription,
            productUpdateQuantity: this.viewController.productUpdateQuantity,
            ...clickableProps,
            forceLazyLoadImage,
            lazyLoadImageDesktop,
            lazyLoadImageMobile,
            componentType: ComponentTypes.PRODUCT_BRIEF
        };
        const { isProductQuickViewOpen, selectedSku } = this.viewController.data;

        const skuBaseId = selectedSku.perlgem.SKU_BASE_ID || selectedSku.skuId;
        const productCode = selectedSku.skuId;
        const mediumImg = selectedSku?.media?.medium[0]?.src;

        return (
            <>
                <ProductBriefWrapper
                    className={productBriefClassNames}
                    ref={this.productBriefRef}
                    data-sku-base-id={skuBaseId}
                    data-product-id={productId}
                    data-test-id={PRODUCT_BRIEF}
                    data-product-code={productCode}
                    data-medium-img={mediumImg}
                >
                    {Object.keys(this.productBriefOrder).map((sectionName: string) => (
                        <div className={classes(`${sectionName}-section`)} key={sectionName}>
                            <OrderRender
                                data-test-id={`${sectionName}-section`}
                                orderConfig={this.productBriefOrder[sectionName]}
                                serviceName={'elc-service-prodcat'}
                                viewRegistry={getViewRegistry<IProductBriefFeature>(featureConfig)}
                            />
                        </div>
                    ))}
                </ProductBriefWrapper>
                {isProductQuickViewOpen && this.renderQuickView()}
            </>
        );
    }

    private renderBriefWrapper() {
        const { maxHeight = 0 } = this.props.config;
        const briefWrapperClassNames = classes('product-brief-wrapper');

        return (
            <CheckVisibility onVisible={this.onVisible}>
                <InitialWrapper className={briefWrapperClassNames} maxHeight={maxHeight} />
            </CheckVisibility>
        );
    }

    public render() {
        const { lazyLoadGridItem } = this.props.config as IProductBriefConfig;

        return lazyLoadGridItem && !this.isItemInView && !root.__ssr
            ? this.renderBriefWrapper()
            : this.renderBrief();
    }
}

ProductBrief.displayName = 'ProductBrief';

export default ProductBrief;
